import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Bell, BellSlash, Lightning } from "@phosphor-icons/react";
import { enablePush, disablePush, isPushSupported, isIosLike, isStandalone, permissionState } from "../lib/push";

/**
 * NotificationPrefsCard — Phase C user-facing notification controls.
 *
 *  - Master toggle (off → unsubscribes every device on this account)
 *  - Per-type toggles (daily summary, truck-live, high activity)
 *  - Subscribe-on-this-device CTA when no active subs
 *  - iOS Safari coachmark when not running as PWA
 *
 * State of truth lives on the server (`/push/preferences`); the component
 * just mirrors it. Optimistic UI: toggles flip immediately, then we POST
 * the merge — failures rollback with a toast.
 */
const TYPE_ROWS = [
  { key: "daily_summary", title: "Daily lunch summary", desc: "10:30am heads-up about today's trucks." },
  { key: "live_alerts", title: "Truck goes live", desc: "Get pinged the moment a truck you might like opens." },
  { key: "high_activity", title: "Big lunch days", desc: "Only when ≥5 trucks are live near you." },
];

export default function NotificationPrefsCard() {
  const [prefs, setPrefs] = useState(null);
  const [activeSubs, setActiveSubs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  const refresh = () => {
    setLoading(true);
    api
      .get("/push/preferences")
      .then(({ data }) => {
        setPrefs(data.prefs);
        setActiveSubs(data.active_subscriptions || 0);
      })
      .catch((e) => toast.error(formatApiError(e) || "Couldn't load preferences"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    if (isPushSupported()) setPerm(permissionState());
  }, []);

  const toggle = async (key) => {
    if (!prefs || busy) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);  // optimistic
    setBusy(true);
    try {
      const { data } = await api.put("/push/preferences", { [key]: next[key] });
      setPrefs(data.prefs);
      setActiveSubs(data.active_subscriptions || 0);
    } catch (e) {
      setPrefs(prefs); // rollback
      toast.error(formatApiError(e) || "Couldn't save preferences");
    } finally {
      setBusy(false);
    }
  };

  const onEnableThisDevice = async () => {
    setBusy(true);
    const res = await enablePush();
    setBusy(false);
    if (res.ok) {
      toast.success("This device is now subscribed");
      setPerm("granted");
      refresh();
    } else if (res.reason === "ios_needs_pwa") {
      toast.error("On iPhone, add the site to your Home Screen first.");
    } else if (res.reason === "denied") {
      toast.error("Notifications blocked. Re-enable in your browser settings.");
    } else if (res.reason === "unsupported") {
      toast.error("Push isn't supported in this browser.");
    } else {
      toast.error(`Couldn't enable: ${res.reason || "unknown"}`);
    }
  };

  const onDisableThisDevice = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success("This device unsubscribed.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't unsubscribe");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !prefs) {
    return (
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="settings-notif-prefs">
        <h2 className="font-display font-bold text-base">Notifications</h2>
        <p className="text-sm text-muted-foreground mt-1">Loading…</p>
      </section>
    );
  }

  const masterOn = !!prefs.master_enabled;
  const iosNoPwa = isIosLike() && !isStandalone();
  const supported = isPushSupported();

  return (
    <section className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="settings-notif-prefs">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-base inline-flex items-center gap-2">
            <Bell weight="duotone" className="h-5 w-5 text-primary" />
            Notifications
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {activeSubs > 0
              ? `Subscribed on ${activeSubs} device${activeSubs === 1 ? "" : "s"}`
              : masterOn
                ? "Not subscribed on this device yet"
                : "Notifications are off"}
          </p>
        </div>
        {/* Master toggle */}
        <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="notif-master-toggle">
          <span className="text-xs font-semibold text-muted-foreground">{masterOn ? "On" : "Off"}</span>
          <span className="relative inline-flex h-6 w-11">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={masterOn}
              disabled={busy}
              onChange={() => toggle("master_enabled")}
              data-testid="notif-master-checkbox"
            />
            <span className="absolute inset-0 rounded-full bg-muted peer-checked:bg-primary transition-colors" />
            <span className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-card shadow transition-transform peer-checked:translate-x-5" />
          </span>
        </label>
      </header>

      {/* Per-type toggles — disabled (greyed out) when master is off */}
      <div className={`space-y-2 ${masterOn ? "" : "opacity-50 pointer-events-none"}`}>
        {TYPE_ROWS.map((row) => (
          <label
            key={row.key}
            className="flex items-start justify-between gap-3 rounded-xl border border-border bg-muted/10 p-3 cursor-pointer select-none"
            data-testid={`notif-type-${row.key}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{row.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{row.desc}</div>
            </div>
            <span className="relative inline-flex h-5 w-9 flex-none mt-0.5">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={!!prefs[row.key]}
                disabled={busy || !masterOn}
                onChange={() => toggle(row.key)}
                data-testid={`notif-type-${row.key}-checkbox`}
              />
              <span className="absolute inset-0 rounded-full bg-muted peer-checked:bg-primary transition-colors" />
              <span className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-card shadow transition-transform peer-checked:translate-x-4" />
            </span>
          </label>
        ))}
      </div>

      {/* Subscribe / unsubscribe-this-device CTA */}
      {masterOn && supported && perm !== "denied" && (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-3 text-sm" data-testid="notif-this-device">
          {activeSubs > 0 ? (
            <button
              type="button"
              onClick={onDisableThisDevice}
              disabled={busy}
              className="h-9 px-3 rounded-full border border-border bg-card text-xs font-semibold hover:bg-muted disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="notif-unsubscribe-device"
            >
              <BellSlash weight="duotone" className="h-3.5 w-3.5" />
              Unsubscribe this device
            </button>
          ) : iosNoPwa ? (
            <p className="text-xs text-muted-foreground" data-testid="notif-ios-hint">
              On iPhone Safari, tap the share button and choose
              <span className="font-semibold"> Add to Home Screen</span>, then re-open
              TruckTrackr from your home screen to enable alerts.
            </p>
          ) : (
            <button
              type="button"
              onClick={onEnableThisDevice}
              disabled={busy}
              className="h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="notif-subscribe-device"
            >
              <Lightning weight="duotone" className="h-3.5 w-3.5" />
              Enable on this device
            </button>
          )}
        </div>
      )}

      {/* Permission-denied warning */}
      {masterOn && supported && perm === "denied" && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-700 dark:text-rose-300" data-testid="notif-perm-denied">
          Browser notifications are blocked. Re-enable in your browser settings (lock icon → Notifications).
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">
        Quiet hours 9am–8pm in your local time · max 3 notifications per day.
      </p>
    </section>
  );
}
